import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Link as LinkIcon, ChevronRight, Loader2, Lock,
  AlertCircle, Camera, FileText, Wand2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useTranslation, Trans } from "react-i18next";
import axios from "axios";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { track } from "../lib/analytics";
import { ingestInspiration, fetchUsage } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Recreate — /recreate
 *
 * The "inspiration" entry point. User pastes a YouTube, TikTok, or
 * Instagram URL or describes a look in their own words.
 * describes the look in words; we extract a structured brief and
 * hand it to /capture so the existing selfie → routine → AR mirror
 * pipeline runs with the brief as additional context.
 *
 * Two paths
 *   • Video URL — YouTube / TikTok / Instagram (Pro-only — costs an LLM call)
 *   • Free-text description (free for everyone)
 *
 * Once the brief is built, the user clicks "Take my selfie" which
 * navigates to /capture with the brief in router state. /capture
 * passes it into the existing analyze call. The routine that comes
 * out the other side is the same shape (steps + product_recs) so
 * the AR mirror reads it without any changes.
 */

const Recreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [url, setUrl] = useState("");
  const [desc, setDesc] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [brief, setBrief] = useState(null); // full {brief, source}
  // True iff the user has at least one prior routine on file. When
  // true we offer the "Use my saved profile" path so they can skip
  // taking yet another selfie. Default false until /me/usage loads.
  const [hasFaceProfile, setHasFaceProfile] = useState(false);
  // True while the saved-profile recreate call is in flight.
  const [generating, setGenerating] = useState(false);
  const isPro = !!user?.is_pro;

  // Cheap probe: does this user have a stored face scan we can reuse?
  // We don't block the page on this — the form still renders during
  // the fetch. Worst case (fetch fails) we just default to "no" and
  // show the take-selfie path, which is the safer default.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const u = await fetchUsage();
        if (!cancelled) setHasFaceProfile(!!u?.has_face_profile);
      } catch {
        // silent — defaults to "no profile", which is the safe fallback
      }
    })();
    return () => {
      cancelled = true;
    };
    // We key on `user?.user_id` instead of the full `user` object —
    // the auth context replaces `user` on every refresh, which would
    // otherwise re-fetch the profile on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!url.trim() && !desc.trim()) {
      setError(t("recreate.errorEmpty"));
      return;
    }
    setError(null);
    setLoading(true);
    track("recreate_ingest_started", {
      has_url: !!url.trim(),
      has_desc: !!desc.trim(),
    });
    try {
      const result = await ingestInspiration({
        url: url.trim() || null,
        description: desc.trim() || null,
      });
      setBrief(result);
      track("recreate_ingest_succeeded", {
        kind: result?.source?.kind,
        had_transcript: !!result?.source?.had_transcript,
      });
    } catch (err) {
      const status = err?.response?.status;
      const detail =
        err?.response?.data?.detail ||
        (status === 402
          ? t("recreate.errorPro")
          : t("recreate.errorGeneric"));
      setError(detail);
      track("recreate_ingest_failed", {
        status, has_url: !!url.trim(),
      });
    } finally {
      setLoading(false);
    }
  };

  // After brief is built, the user clicks "Take my selfie" — we
  // navigate to /capture with the brief in router state so the
  // existing capture flow can hand it through to /api/analyze.
  const continueToCapture = () => {
    track("recreate_continue_to_capture");
    navigate("/capture", { state: { inspiration: brief } });
  };

  // Generate the adapted routine WITHOUT a fresh selfie — uses the
  // user's stored skin signals from their most recent routine. This
  // is the "no scan needed" path the founder asked for. On success
  // we navigate to the routine detail page so the user goes straight
  // into the AR mirror handoff.
  const generateFromSavedProfile = async () => {
    if (generating) return;
    setGenerating(true);
    track("recreate_continue_from_saved_profile");
    try {
      const { data } = await axios.post(
        `${API}/analyze/from-saved-profile`,
        {
          inspiration: brief?.brief || brief,
          goal: "everyday",
          experience: "beginner",
        },
        { withCredentials: true },
      );
      track("recreate_routine_generated", {
        routine_id: data?.id,
        via: "saved_profile",
      });
      navigate(`/routine/${data.id}`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // 409 with kind=need_face_scan → user has no prior scan;
      // route them gracefully to the scan path with a friendly toast.
      if (status === 409 && detail?.kind === "need_face_scan") {
        toast.info(detail?.message || t("recreate.needFaceScanToast"));
        continueToCapture();
        return;
      }
      // 402 → paywall (Pro-only flow when applicable).
      if (status === 402) {
        navigate("/pricing", { state: { reason: "recreate_saved_profile" } });
        return;
      }
      toast.error(
        typeof detail === "string"
          ? detail
          : t("recreate.savedProfileFailToast"),
      );
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen trubeaute-gradient">
      <Header />
      <main
        className="pt-24 sm:pt-28 px-5 sm:px-8 max-w-3xl mx-auto pb-16"
        data-testid="recreate-page"
      >
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-[#9B6A6C]" />
          <span className="text-[11px] uppercase tracking-[0.28em] text-[#9B6A6C]">
            {t("recreate.kicker")}
          </span>
        </div>
        <h1 className="font-serif text-[#2C2123] leading-tight text-4xl sm:text-5xl mb-4">
          {t("recreate.headlineLine1")} <br className="hidden sm:block" />
          <span className="italic">{t("recreate.headlineLine2")}</span>
        </h1>
        <p className="text-[#5C4B51] text-base sm:text-lg mb-9 leading-relaxed max-w-xl">
          <Trans
            i18nKey="recreate.intro"
            components={{ em: <em /> }}
          />
          {hasFaceProfile && (
            <span className="block mt-2 text-sm text-[#9B6A6C]">
              {t("recreate.introSavedProfile")}
            </span>
          )}
        </p>

        {!brief && (
          <form
            onSubmit={submit}
            className="rounded-3xl bg-white/85 border border-[#EADDD7] p-6 sm:p-7 shadow-[0_24px_60px_-30px_rgba(155,106,108,0.35)]"
            data-testid="recreate-form"
          >
            {/* Video URL input — all three platforms supported (YouTube,
                TikTok, Instagram) so the label is platform-agnostic. */}
            <label className="block mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs uppercase tracking-[0.22em] text-[#9B6A6C] inline-flex items-center gap-1.5">
                  <LinkIcon className="h-3 w-3" />
                  {t("recreate.urlLabel")}
                </span>
                {!isPro && (
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#C9A8A4] inline-flex items-center gap-1">
                    <Lock className="h-3 w-3" />
                    Pro
                  </span>
                )}
              </div>
              <div className="relative">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#C9A8A4]" />
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  disabled={loading}
                  className="w-full rounded-xl border border-[#EADDD7] pl-10 pr-4 py-2.5 bg-white text-[#2C2123] focus:outline-none focus:border-[#9B6A6C] transition-colors"
                  placeholder={t("recreate.urlPlaceholder")}
                  data-testid="recreate-url-input"
                  maxLength={500}
                />
              </div>
              <p className="text-[11px] text-[#C9A8A4] mt-1.5">
                {t("recreate.urlHelp")}
              </p>
            </label>

            {/* OR divider */}
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-[#EADDD7]" />
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#C9A8A4]">{t("recreate.or")}</span>
              <div className="flex-1 h-px bg-[#EADDD7]" />
            </div>

            {/* Free-text description */}
            <label className="block mb-5">
              <span className="text-xs uppercase tracking-[0.22em] text-[#9B6A6C] mb-1.5 block inline-flex items-center gap-1.5">
                <FileText className="h-3 w-3" />
                {t("recreate.descLabel")}
              </span>
              <textarea
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                disabled={loading}
                rows={4}
                maxLength={1500}
                className="w-full rounded-xl border border-[#EADDD7] px-4 py-3 bg-white text-[#2C2123] focus:outline-none focus:border-[#9B6A6C] transition-colors text-[15px] leading-relaxed resize-none"
                placeholder={t("recreate.descPlaceholder")}
                data-testid="recreate-desc-input"
              />
            </label>

            {error && (
              <div
                className="flex items-start gap-2 text-sm text-[#9B6A6C] mb-4 bg-[#FBEDE9] border border-[#E4D2DC] rounded-xl p-3"
                data-testid="recreate-error"
              >
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (!url.trim() && !desc.trim())}
              data-testid="recreate-submit"
              className="w-full bg-[#9B6A6C] hover:bg-[#5D4050] disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-3.5 rounded-full font-medium text-sm transition-colors inline-flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("recreate.loading")}
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  {t("recreate.submit")}
                </>
              )}
            </button>
            <p className="text-[11px] text-[#9B6A6C] text-center mt-4">
              {t("recreate.creditFooter")}
            </p>
          </form>
        )}

        <AnimatePresence>
          {brief && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="rounded-3xl bg-white/85 border border-[#EADDD7] p-6 sm:p-7 shadow-[0_24px_60px_-30px_rgba(155,106,108,0.35)]"
              data-testid="recreate-brief"
            >
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-[#9B6A6C]" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-[#9B6A6C]">
                  {t("recreate.briefKicker")}
                </span>
              </div>
              <h2
                className="font-serif text-[#2C2123] text-2xl mb-3 leading-snug"
                data-testid="recreate-brief-name"
              >
                {brief.brief?.look_name || t("recreate.briefFallbackName")}
              </h2>
              {brief.brief?.vibe && (
                <p className="text-[#5C4B51] text-sm sm:text-base mb-4 leading-relaxed">
                  <span className="font-medium text-[#9B6A6C] mr-1">{t("recreate.vibeLabel")}</span>
                  {brief.brief.vibe}
                </p>
              )}
              {brief.brief?.tutorial_summary && (
                <p
                  className="text-[#5C4B51] text-sm sm:text-base mb-5 leading-relaxed italic"
                  data-testid="recreate-brief-summary"
                >
                  {brief.brief.tutorial_summary}
                </p>
              )}

              <div className="grid sm:grid-cols-2 gap-3 mb-6">
                {brief.brief?.colour_family && (
                  <div className="bg-[#FBF4F2] border border-[#EADDD7] rounded-xl p-3">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#9B6A6C] mb-1">
                      {t("recreate.colourFamilyLabel")}
                    </div>
                    <div className="text-sm text-[#2C2123]">
                      {brief.brief.colour_family}
                    </div>
                  </div>
                )}
                {brief.brief?.finish && (
                  <div className="bg-[#FBF4F2] border border-[#EADDD7] rounded-xl p-3">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#9B6A6C] mb-1">
                      {t("recreate.finishLabel")}
                    </div>
                    <div className="text-sm text-[#2C2123] capitalize">
                      {brief.brief.finish}
                    </div>
                  </div>
                )}
              </div>

              {brief.brief?.techniques?.length > 0 && (
                <div className="mb-6">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#9B6A6C] mb-2">
                    {t("recreate.techniquesLabel")}
                  </div>
                  <ul className="space-y-1.5">
                    {brief.brief.techniques.slice(0, 5).map((t, i) => (
                      <li
                        key={i}
                        className="text-sm text-[#5C4B51] flex items-start gap-2"
                      >
                        <ChevronRight className="h-3.5 w-3.5 text-[#9B6A6C] flex-shrink-0 mt-0.5" />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* ── Smart CTAs ───────────────────────────────────
                  Two paths after the brief renders:
                    1. User has a stored face profile (≥1 prior routine):
                       PRIMARY → "Use my saved profile" (instant, no scan)
                       SECONDARY → "Re-scan my face" (skin has changed)
                    2. User has no prior scan:
                       Single CTA → "Take my selfie" (forced — we need
                       features to adapt this look to their face).
                  Founder's exact ask: don't make returning users scan
                  again unless their skin has changed dramatically. */}
              {hasFaceProfile ? (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={generateFromSavedProfile}
                      disabled={generating}
                      data-testid="recreate-use-saved-profile"
                      className="bg-[#9B6A6C] hover:bg-[#5D4050] disabled:opacity-60 text-white px-6 py-3 rounded-full font-medium text-sm transition-colors inline-flex items-center gap-2 shadow-[0_8px_20px_rgba(155,106,108,0.32)]"
                    >
                      {generating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Wand2 className="h-4 w-4" />
                      )}
                      {generating
                        ? t("recreate.savedProfileGenerating")
                        : t("recreate.savedProfileCta")}
                    </button>
                    <button
                      type="button"
                      onClick={continueToCapture}
                      disabled={generating}
                      data-testid="recreate-rescan"
                      className="bg-white hover:bg-[#FBF4F2] disabled:opacity-60 text-[#9B6A6C] px-5 py-3 rounded-full font-medium text-sm transition-colors inline-flex items-center gap-2 border border-[#EADDD7]"
                    >
                      <Camera className="h-4 w-4" />
                      {t("recreate.rescanCta")}
                    </button>
                  </div>
                  <p className="text-[11px] text-[#8A7D82] leading-relaxed">
                    {t("recreate.savedProfileHelp")}
                  </p>
                </div>
              ) : (
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={continueToCapture}
                    data-testid="recreate-continue"
                    className="bg-[#9B6A6C] hover:bg-[#5D4050] text-white px-6 py-3 rounded-full font-medium text-sm transition-colors inline-flex items-center gap-2"
                  >
                    <Camera className="h-4 w-4" />
                    {t("recreate.takeSelfieCta")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setBrief(null);
                      setUrl("");
                      setDesc("");
                    }}
                    className="text-sm text-[#9B6A6C] hover:text-[#5D4050] underline-offset-4 hover:underline transition-colors"
                    data-testid="recreate-restart"
                  >
                    {t("recreate.tryDifferentCta")}
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default Recreate;
